import React, { useEffect, useState } from 'react';
import { getAgents, deleteAgent } from '../api/mongoServices';
import RechargeModal from './RechargeModal';
import PerformanceModal from './PerformanceModal';
import BetEntriesModal from './BetEntriesModal';
import AgentFormModal from './AgentFormModal';
import { motion } from 'framer-motion';
import { FiPlus, FiRefreshCw, FiTrendingUp, FiDollarSign, FiUser, FiTrash2, FiEdit2 } from 'react-icons/fi';

const AgentList = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);
  const [showBetEntries, setShowBetEntries] = useState(false);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadAgents = async () => {
    setLoading(true);
    const data = await getAgents();
    setAgents(data);
    setLoading(false);
  };

  useEffect(() => { loadAgents(); }, []);

  const totalBalance = agents.reduce((sum, agent) => sum + (agent.balance || 0), 0);
  const activeAgents = agents.length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-between items-start mb-12"
        >
          <div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
              Agent Management
            </h1>
            <p className="text-gray-400 text-lg">Monitor and manage all your agents in one place</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold shadow-lg transition"
            onClick={() => { setShowAgentForm(true); setEditMode(false); setSelectedAgent(null); }}
          >
            <FiPlus size={20} /> Add New Agent
          </motion.button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl hover:border-blue-500 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Total Agents</p>
                <p className="text-4xl font-bold text-blue-400 mt-2">{activeAgents}</p>
              </div>
              <FiUser size={40} className="text-blue-400 opacity-50" />
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl hover:border-purple-500 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Total Balance</p>
                <p className="text-4xl font-bold text-purple-400 mt-2">{totalBalance.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Points</p>
              </div>
              <FiDollarSign size={40} className="text-purple-400 opacity-50" />
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl hover:border-pink-500 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Avg. Balance</p>
                <p className="text-4xl font-bold text-pink-400 mt-2">{agents.length > 0 ? Math.round(totalBalance / agents.length).toLocaleString() : '0'}</p>
                <p className="text-xs text-gray-500 mt-1">Per Agent</p>
              </div>
              <FiTrendingUp size={40} className="text-pink-400 opacity-50" />
            </div>
          </motion.div>
        </motion.div>

        {/* Agents Grid */}
        {loading ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="flex justify-center items-center py-20"
          >
            <FiRefreshCw size={40} className="text-blue-400" />
          </motion.div>
        ) : agents.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-16 text-center"
          >
            <p className="text-gray-400 text-lg">No agents found. Create one to get started!</p>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {agents.map((agent, index) => (
              <motion.div
                key={agent.number}
                variants={itemVariants}
                whileHover={{ y: -5, boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)' }}
                className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-lg hover:border-purple-500 transition relative overflow-hidden group"
              >
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 opacity-0 group-hover:opacity-10 transition rounded-xl"></div>

                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white group-hover:text-blue-300 transition">{agent.name}</h3>
                      <p className="text-gray-400 text-sm">ID: {agent.number}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${agent.role === 'super_agent' ? 'bg-purple-900 text-purple-300' : 'bg-blue-900 text-blue-300'}`}>
                      {agent.role}
                    </span>
                  </div>

                  {/* Balance Section */}
                  <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-400 text-xs font-medium mb-1">Current Balance</p>
                    <p className="text-2xl font-bold text-green-400">{agent.balance.toLocaleString()} pts</p>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                      onClick={() => { setSelectedAgent(agent); setShowRecharge(true); }}
                    >
                      💰 Recharge
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                      onClick={() => { setSelectedAgent(agent); setShowPerformance(true); }}
                    >
                      📊 Performance
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                      onClick={() => { setSelectedAgent(agent); setShowBetEntries(true); }}
                    >
                      🎲 Bets
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                      onClick={() => { setSelectedAgent(agent); setEditMode(true); setShowAgentForm(true); }}
                    >
                      <FiEdit2 size={14} /> Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="col-span-2 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                      onClick={async () => {
                        await deleteAgent(agent.number);
                        loadAgents();
                      }}
                    >
                      <FiTrash2 size={14} /> Delete
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Modals */}
        {showRecharge && selectedAgent && (
          <RechargeModal
            agent={selectedAgent}
            onClose={() => setShowRecharge(false)}
            onSuccess={() => { loadAgents(); setShowRecharge(false); }}
          />
        )}
        {showPerformance && selectedAgent && (
          <PerformanceModal
            agent={selectedAgent}
            onClose={() => setShowPerformance(false)}
          />
        )}
        {showBetEntries && selectedAgent && (
          <BetEntriesModal
            agent={selectedAgent}
            onClose={() => setShowBetEntries(false)}
          />
        )}
        {showAgentForm && (
          <AgentFormModal
            agent={editMode ? selectedAgent : null}
            onClose={() => setShowAgentForm(false)}
            onSuccess={() => { loadAgents(); setShowAgentForm(false); }}
          />
        )}
      </motion.div>
    </div>
  );
};

export default AgentList;
